// Keyboard cheat sheet (?) and the About dialog with the licences of everything that ships (§11 M11).
import { useT, useLang, type MessageKey } from '../../i18n';
import { useUi } from '../../state/ui';
import licenses from '../../about/licenses.json';
import assets from '../../about/assets.json';
import s from './Dialog.module.css';

const SHORTCUTS: [string, MessageKey][] = [
  ['← →', 'kPrevNext'],
  ['0–5', 'kRate'],
  ['X', 'kReject'],
  ['E / F', 'kCull'],
  ['\\', 'kBefore'],
  ['Y', 'kSplit'],
  ['J', 'kClipping'],
  ['Z', 'kZoom'],
  ['R', 'kCrop'],
  ['O', 'kMaskOverlay'],
  ['B', 'kRemoveBg'],
  ['W', 'kBgView'],
  ['C / Shift+C', 'kCopy'],
  ['V / Shift+V', 'kPaste'],
  ['Ctrl+Z / Ctrl+Shift+Z', 'kUndoRedo'],
  ['Ctrl+E', 'kExport'],
  ['Alt', 'kAlt'],
  ['Space', 'kPan'],
  ['Esc / Enter', 'kEscEnter'],
  ['?', 'kCheat'],
];

function Frame({
  id,
  title,
  onClose,
  children,
}: {
  id: string;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const t = useT();
  return (
    <div
      className={s.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${id}-title`}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className={s.dialog}
        data-testid={id}
        style={{ maxWidth: 620, maxHeight: 'calc(100vh - 40px)', overflow: 'auto' }}
      >
        <h2 id={`${id}-title`}>{title}</h2>
        {children}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <button onClick={onClose} autoFocus>
            {t('close')}
          </button>
        </div>
      </div>
    </div>
  );
}

export function CheatSheet() {
  const t = useT();
  const open = useUi((u) => u.cheatOpen);
  if (!open) return null;
  return (
    <Frame
      id="cheat-sheet"
      title={t('cheatTitle')}
      onClose={() => useUi.getState().set({ cheatOpen: false })}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <tbody>
          {SHORTCUTS.map(([k, label]) => (
            <tr key={k} style={{ borderBottom: '1px solid var(--line)' }}>
              <td style={{ padding: '4px 12px 4px 0', whiteSpace: 'nowrap' }}>
                <kbd>{k}</kbd>
              </td>
              <td style={{ padding: '4px 0' }}>{t(label)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Frame>
  );
}

// Model weights and fonts with their licences, written by scripts/check-licenses.ts (npm test).
const MODELS = assets.filter((a) => a.kind === 'model');
const FONTS = [...new Set(assets.filter((a) => a.kind === 'font').map((a) => a.name.replace(/ \d+$/, '')))];

export function AboutDialog() {
  const t = useT();
  const lang = useLang((l) => l.lang);
  const open = useUi((u) => u.aboutOpen);
  if (!open) return null;
  return (
    <Frame
      id="about-dialog"
      title={t('aboutTitle')}
      onClose={() => useUi.getState().set({ aboutOpen: false })}
    >
      <p>{t('aboutText')}</p>
      <p style={{ fontSize: 13, opacity: 0.8 }}>
        {t('aboutVersion', { v: __APP_VERSION__ })} ·{' '}
        <a href={`/help/${lang}/index.html`} target="_blank" rel="noreferrer">
          {t('help')}
        </a>
      </p>
      <h3 style={{ fontSize: 14 }}>{t('aboutModels')}</h3>
      <ul style={{ fontSize: 13, paddingLeft: 18 }}>
        {MODELS.map((m) => (
          <li key={m.name}>
            {m.name} — {m.license} ({m.source})
          </li>
        ))}
      </ul>
      <h3 style={{ fontSize: 14 }}>{t('aboutFonts')}</h3>
      <p style={{ fontSize: 13 }} data-testid="about-fonts">
        {FONTS.join(', ')} — SIL Open Font License 1.1 (fontsource.org)
      </p>
      <h3 style={{ fontSize: 14 }}>{t('aboutLicenses', { n: licenses.length })}</h3>
      <p style={{ fontSize: 12, opacity: 0.8 }}>{t('aboutLgpl')}</p>
      <ul data-testid="about-licenses" style={{ fontSize: 12, columns: 2, paddingLeft: 18 }}>
        {licenses.map((l) => (
          <li key={l.name}>
            {l.name} {l.version} — {l.license}
          </li>
        ))}
      </ul>
    </Frame>
  );
}
